import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { CommonPipes } from 'app/trello/common-pipes.imports';
import { ReportRangeSelectorComponent } from 'app/trello/components/shared/report-range-selector/report-range-selector.component';
import { ChangeChartWithTableComponent } from 'app/trello/components/shared/visualisations/change-chart-with-table/change-chart-with-table.component';
import { DateRangeHeatmapChartWithTableComponent } from 'app/trello/components/shared/visualisations/daily-heatmap-chart-with-table/date-range-heatmap-chart-with-table.component';
import { DurationsChartWithTableComponent } from 'app/trello/components/shared/visualisations/durations-chart-with-table/durations-chart-with-table.component';
import { MinAvgMaxBarChartWithTableComponent } from 'app/trello/components/shared/visualisations/min-avg-max-bar-chart-with-table/min-avg-max-bar-chart-with-table.component';
import { MinAvgMaxPointChartWithTableComponent } from 'app/trello/components/shared/visualisations/min-avg-max-point-chart-with-table/min-avg-max-point-chart-with-table.component';
import { PieChartWithTableComponent } from 'app/trello/components/shared/visualisations/pie-chart-with-table/pie-chart-with-table.component';
import { ProgressionSplitChartComponent } from 'app/trello/components/shared/visualisations/progression-split-chart/progression-split-chart.component';
import { TotalsInsightPanelComponent } from 'app/trello/components/shared/visualisations/totals-insight-panel/totals-insight-panel.component';
import { TrendChartWithTableComponent } from 'app/trello/components/shared/visualisations/trend-chart-with-table/trend-chart-with-table.component';
import { boardStages } from 'app/trello/models/board-stages';
import { BoardSummary, CardReference, GroupedMap, GroupedMap2D, Statistics } from 'app/trello/models/board-summary';
import { AttachmentChartData, CardChartData, ChartableData, CommentChartData, LabelChartData, LabelledMinAvgMaxValues, LabelledValues, ListChartData, MemberChartData, OverdueChartData, StageChartData } from 'app/trello/models/charts';
import { DateRange } from 'app/trello/models/date-range';
import { Label } from 'app/trello/models/labels';
import { List } from 'app/trello/models/lists';
import { Member } from 'app/trello/models/members';
import { PrimeNg } from 'app/trello/primeng.imports';
import { AttachmentChartDataAggregator } from 'app/trello/services/charts/attachment-chart-data-aggregator';
import { CardChartDataAggregator } from 'app/trello/services/charts/card-chart-data-aggregator';
import { CommentChartDataAggregator } from 'app/trello/services/charts/comment-chart-data-aggregator';
import { LabelChartDataAggregator } from 'app/trello/services/charts/label-chart-data-aggregator';
import { ListChartDataAggregator } from 'app/trello/services/charts/list-chart-data-aggregator';
import { MemberChartDataAggregator } from 'app/trello/services/charts/member-chart-data-aggregator';
import { OverdueChartDataAggregator } from 'app/trello/services/charts/overdue-chart-data-aggregator';
import { StageChartDataAggregator } from 'app/trello/services/charts/stage-chart-data-aggregator';
import { GranularNowProvider } from 'app/trello/services/granular-now-provider';
import { TrelloService } from 'app/trello/services/trello.service';
import { boardSummaryReportDateRangeSelected, boardSummaryReportOpened } from 'app/trello/store/board-summary-report/board-summary-report.actions';
import {
  selectBoardSummary,
  selectBoardSummaryAllLoaded,
  selectBoardSummaryBasicLoaded,
  selectBoardSummaryDateRange,
  selectComparableBoardSummary,
  selectComparableBoardSummaryDateRange,
  selectLabels,
  selectLists,
  selectMembers
} from 'app/trello/store/board-summary-report/board-summary-report.selectors';
import { Templating } from 'app/trello/templating.imports';
import { resolveColor } from 'app/trello/utility/resolve-color';
import { StatisticsExtractor } from 'app/trello/utility/statistics-extractor';
import { someKeys } from 'app/trello/utility/utility-functions';
import cloneDeep from 'lodash-es/cloneDeep';
import { DateTime } from 'luxon';
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion';

@Component({
  imports: [
    CommonPipes,
    PrimeNg,
    Templating,
    Accordion,
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    ChangeChartWithTableComponent,
    DateRangeHeatmapChartWithTableComponent,
    DurationsChartWithTableComponent,
    FormsModule,
    MinAvgMaxBarChartWithTableComponent,
    MinAvgMaxPointChartWithTableComponent,
    PieChartWithTableComponent,
    ProgressionSplitChartComponent,
    ReportRangeSelectorComponent,
    TotalsInsightPanelComponent,
    TrendChartWithTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-board-summary-reference-component',
  templateUrl: './board-summary-reference.component.html',
  styleUrls: ['./board-summary-reference.component.css'],
})
@UntilDestroy()
export class BoardSummaryReferenceComponent implements OnInit {

  protected readonly aggregationLevels: any[] = [
    {label: 'Daily', value: 'day'},
    {label: 'Weekly', value: 'week'},
    {label: 'Monthly', value: 'month'},
  ];

  protected readonly someKeys = someKeys;

  protected initialDateRange: DateRange | undefined;

  protected selectedAggregationLevel: 'day' | 'week' | 'month' = 'day';

  protected cardChartData!: CardChartData;
  protected overdueChartData!: OverdueChartData;
  protected commentChartData!: CommentChartData;
  protected attachmentChartData!: AttachmentChartData;
  protected labelChartDataById: Map<string, LabelChartData> = new Map();
  protected listChartDataById: Map<string, ListChartData> = new Map();
  protected memberChartDataById: Map<string, MemberChartData> = new Map();
  protected stageChartDataById: Map<string, StageChartData> = new Map();

  protected combinedLabelAddedChartDataByLevel!: ChartableData<LabelledValues, GroupedMap2D<CardReference[]>>[];
  protected combinedLabelCountChartDataByLevel!: ChartableData<LabelledValues, GroupedMap<CardReference[]>>[];
  protected combinedLabelCountChartDataByPeriod!: ChartableData<LabelledValues, never>;
  protected combinedLabelDurationChartData!: ChartableData<LabelledValues, GroupedMap2D<number>>[];
  protected combinedLabelDurationMinAvgMaxChartData!: ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>[];
  protected cycleTimeByCompletionDateChartData!: ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>;
  protected cycleTimeByMemberChartData!: ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>;
  protected boardActivityHeatmapData!: { [date: string]: any[] };

  protected progressionChartData: {
    [key: string]: {
      label: string,
      percentage: number,
      backgroundColor?: string,
    },
  } = {};

  private _reportNow: string | null = null;

  private _stats: BoardSummary | null = null;
  private _dateRange: DateRange | null = null;

  private _comparableStats: BoardSummary | null = null;
  private _comparableDateRange: DateRange | null = null;

  private _labels: Label[] = [];
  private _lists: List[] = [];
  private _members: Member[] = [];

  private _basicDataLoaded: boolean = false;
  private _allDataLoaded: boolean = false;

  public get reportNow(): string | undefined {
    return this._reportNow || undefined;
  }

  public get stats(): BoardSummary | undefined {
    return this._stats || undefined;
  }

  public get dateRange(): DateRange | undefined {
    return this._dateRange || undefined;
  }

  public get comparableStats(): BoardSummary | undefined {
    return this._comparableStats || undefined;
  }

  public get comparableDateRange(): DateRange | undefined {
    return this._comparableDateRange || undefined;
  }

  public get labels(): Label[] {
    return this._labels;
  }

  public get lists(): List[] {
    return this._lists;
  }

  public get members(): Member[] {
    return this._members;
  }

  public get stages(): string[] {
    return boardStages();
  }

  public get basicDataLoaded(): boolean {
    return this._basicDataLoaded;
  }

  public get allDataLoaded(): boolean {
    return this._allDataLoaded;
  }

  constructor(
    //
    private readonly changeDetector: ChangeDetectorRef,
    private readonly store: Store,
    //
    private readonly cardChartDataAggregator: CardChartDataAggregator,
    private readonly overdueChartDataAggregator: OverdueChartDataAggregator,
    private readonly commentChartDataAggregator: CommentChartDataAggregator,
    private readonly attachmentChartDataAggregator: AttachmentChartDataAggregator,
    private readonly labelChartDataAggregator: LabelChartDataAggregator,
    private readonly listChartDataAggregator: ListChartDataAggregator,
    private readonly memberChartDataAggregator: MemberChartDataAggregator,
    private readonly stageChartDataAggregator: StageChartDataAggregator,
    //
    private readonly nowProvider: GranularNowProvider,
    private readonly statisticsExtractor: StatisticsExtractor,
    //
    private readonly trelloService: TrelloService, // required
    //
  ) {
  }

  public ngOnInit(): void {

    this.initialDateRange = {
      rangeType: 'lastDays28',
    };

    this.selectedAggregationLevel = 'day';

    this._reportNow = this.nowProvider.getNow();

    this.store.dispatch(boardSummaryReportOpened({now: this._reportNow}));

    setTimeout(() => this.subscribeToBoardSummaryData());
  }

  protected selectAggregationLevel(
    level: 'day' | 'week' | 'month',
  ): void {

    this.selectedAggregationLevel = level;

    this.aggregateChartData();
  }

  protected selectDateRange(
    dateRange: DateRange,
  ): void {

    if (dateRange === undefined) {
      return;
    }

    this.store.dispatch(boardSummaryReportDateRangeSelected({dateRange, now: this._reportNow!}));
  }

  protected labelColor(
    labelId: string,
  ): string {

    if (!this.basicDataLoaded) {
      return '';
    }

    const label = this.labels.find(label => label.labelId === labelId);

    return label?.color || '';
  }

  protected labelResolvedColor(
    labelId: string,
  ): string {

    const labelColor = this.labelColor(labelId);

    switch (labelColor) {

      case 'green_light':
        return resolveColor('var(--ds-background-accent-green-subtler, #164B35)');

      case 'green':
        return resolveColor('var(--ds-background-accent-green-subtle, #216E4E)');

      case 'green_dark':
        return resolveColor('var(--ds-background-accent-green-bolder, #4BCE97)');

      case 'yellow_light':
        return resolveColor('var(--ds-background-accent-yellow-subtler, #533F04)');

      case 'yellow':
        return resolveColor('var(--ds-background-accent-yellow-subtle, #7F5F01)');

      case 'yellow_dark':
        return resolveColor('var(--ds-background-accent-yellow-bolder, #E2B203)');

      case 'orange_light':
        return resolveColor('var(--ds-background-accent-orange-subtler, #702E00)');

      case 'orange':
        return resolveColor('var(--ds-background-accent-orange-subtle, #A54800)');

      case 'orange_dark':
        return resolveColor('var(--ds-background-accent-orange-bolder, #FEA362)');

      case 'red_light':
        return resolveColor('var(--ds-background-accent-red-subtler, #5D1F1A)');

      case 'red':
        return resolveColor('var(--ds-background-accent-red-subtle, #AE2E24)');

      case 'red_dark':
        return resolveColor('var(--ds-background-accent-red-bolder, #F87168)');

      case 'purple_light':
        return resolveColor('var(--ds-background-accent-purple-subtler, #352C63)');

      case 'purple':
        return resolveColor('var(--ds-background-accent-purple-subtle, #5E4DB2)');

      case 'purple_dark':
        return resolveColor('var(--ds-background-accent-purple-bolder, #9F8FEF)');

      case 'blue_light':
        return resolveColor('var(--ds-background-accent-blue-subtler, #09326C)');

      case 'blue':
        return resolveColor('var(--ds-background-accent-blue-subtle, #0055CC)');

      case 'blue_dark':
        return resolveColor('var(--ds-background-accent-blue-bolder, #579DFF)');

      case 'sky_light':
        return resolveColor('var(--ds-background-accent-teal-subtler, #164555)');

      case 'sky':
        return resolveColor('var(--ds-background-accent-teal-subtle, #206A83)');

      case 'sky_dark':
        return resolveColor('var(--ds-background-accent-teal-bolder, #6CC3E0)');

      case 'lime_light':
        return resolveColor('var(--ds-background-accent-lime-subtler, #37471F)');

      case 'lime':
        return resolveColor('var(--ds-background-accent-lime-subtle, #4C6B1F)');

      case 'lime_dark':
        return resolveColor('var(--ds-background-accent-lime-bolder, #94C748)');

      case 'pink_light':
        return resolveColor('var(--ds-background-accent-magenta-subtler, #50253F)');

      case 'pink':
        return resolveColor('var(--ds-background-accent-magenta-subtle, #943D73)');

      case 'pink_dark':
        return resolveColor('var(--ds-background-accent-magenta-bolder, #E774BB)');

      case 'black_light':
        return resolveColor('var(--ds-background-accent-gray-subtler, #454F59)');

      case 'black':
        return resolveColor('var(--ds-background-accent-gray-subtle, #596773)');

      case 'black_dark':
        return resolveColor('var(--ds-background-accent-gray-bolder, #8C9BAB)');
    }

    return labelColor;
  }

  protected labelName(
    labelId: string,
  ): string {

    if (!this.basicDataLoaded) {
      return '';
    }

    const label = this.labels.find(label => label.labelId === labelId);

    return label?.name || '?';
  }

  protected listName(
    listId: string,
  ): string {

    if (!this.basicDataLoaded) {
      return '';
    }

    const list = this.lists.find(list => list.listId === listId);

    return list?.name || '?';
  }

  protected memberName(
    memberId: string,
  ): string {

    if (!this.basicDataLoaded) {
      return '';
    }

    const member = this.members.find(member => member.memberId === memberId);

    return member?.fullName || '?';
  }

  protected stageName(
    stage: string,
  ): string {

    switch (stage) {

      case 'pending':
        return 'Pending';

      case 'inProgress':
        return 'In Progress';

      case 'completed':
        return 'Completed';

      default:
        return '';
    }
  }

  protected statsForLabel(
    labelId: string,
  ): Statistics {
    return this.stats?.labels[labelId]!;
  }

  protected comparableStatsForLabel(
    labelId: string,
  ): Statistics | undefined {
    // @todo return observables on second data load
    return this.allDataLoaded ? this.comparableStats?.labels[labelId] : undefined;
  }

  protected statsForList(
    listId: string,
  ): Statistics {
    return this.stats?.lists[listId]!;
  }

  protected comparableStatsForList(
    listId: string,
  ): Statistics | undefined {
    // @todo return observables on second data load
    return this.allDataLoaded ? this.comparableStats?.lists[listId] : undefined;
  }

  protected statsForMember(
    memberId: string,
  ): Statistics {
    return this.stats?.members[memberId]!;
  }

  protected comparableStatsForMember(
    memberId: string,
  ): Statistics | undefined {
    // @todo return observables on second data load
    return this.allDataLoaded ? this.comparableStats?.members[memberId] : undefined;
  }

  protected statsForStage(
    stage: string,
  ): Statistics {
    return this.stats?.stages[stage]!;
  }

  protected comparableStatsForStage(
    stage: string,
  ): Statistics | undefined {
    // @todo return observables on second data load
    return this.allDataLoaded ? this.comparableStats?.stages[stage] : undefined;
  }

  private pickBestAggregationLevel(
    dateRange: DateRange,
  ): void {

    if (!dateRange.rangeFrom
      || !dateRange.rangeTo) {

      this.selectedAggregationLevel = 'day';
      return;
    }

    const from =
      DateTime.fromISO(
        dateRange.rangeFrom,
        {
          // @todo handle timezone properly
          setZone: true,
        },
      );

    if (!from.isValid) {
      throw new Error(`Invalid 'rangeFrom' date: ${from.invalidExplanation}`);
    }

    const to =
      DateTime.fromISO(
        dateRange.rangeTo,
        {
          // @todo handle timezone properly
          setZone: true,
        },
      );

    if (!to.isValid) {
      throw new Error(`Invalid 'rangeTo' date: ${to.invalidExplanation}`);
    }

    if (to <= from) {
      throw new Error("'rangeTo' must be after 'rangeFrom'");
    }

    const diffInDays = Math.floor(to.diff(from, 'days').days);

    if (diffInDays <= 31) {
      this.selectedAggregationLevel = 'day';

    } else if (diffInDays <= 182) {
      this.selectedAggregationLevel = 'week';

    } else {
      this.selectedAggregationLevel = 'month';
    }
  }

  private aggregateChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    this.aggregateCardChartData();
    this.aggregateOverdueChartData();
    this.aggregateCommentChartData();
    this.aggregateAttachmentChartData();
    this.aggregateLabelChartData();
    this.aggregateListChartData();
    this.aggregateMemberChartData();
    this.aggregateStageChartData();

    this.aggregateCycleTimeByCompletionDate();
    this.aggregateCycleTimeByMember();
    this.aggregateBoardActivityHeatmapData();

    this.calculateProgressionChartData();

    this.changeDetector.detectChanges();
  }

  private aggregateCardChartData(): void {

    this.cardChartData =
      this.cardChartDataAggregator.aggregate(
        this.stats!.board,
        this.selectedAggregationLevel,
      );
  }

  private aggregateOverdueChartData(): void {

    this.overdueChartData =
      this.overdueChartDataAggregator.aggregate(
        this.stats!.board,
        this.selectedAggregationLevel,
      );
  }

  private aggregateCommentChartData(): void {

    this.commentChartData =
      this.commentChartDataAggregator.aggregate(
        this.stats!.board,
        this.selectedAggregationLevel,
      );
  }

  private aggregateAttachmentChartData(): void {

    this.attachmentChartData =
      this.attachmentChartDataAggregator.aggregate(
        this.stats!.board,
        this.selectedAggregationLevel,
      );
  }

  private aggregateLabelChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    this.labelChartDataById.clear();

    this.labels.forEach((label) => {

      const labelId = label.labelId;

      const stats = this.stats!.labels[labelId];

      if (!stats) {
        return;
      }

      const aggregation =
        this.labelChartDataAggregator.aggregate(
          stats,
          this.selectedAggregationLevel,
        );

      this.labelChartDataById.set(
        labelId,
        aggregation,
      );
    });

    this.combinedLabelAddedChartDataByLevel = this.combineLabelAddedChartDataByLevel();
    this.combinedLabelCountChartDataByLevel = this.combineLabelCountChartDataByLevel();
    this.combinedLabelCountChartDataByPeriod = this.combineLabelCountChartDataByPeriod();
    this.combinedLabelDurationChartData = this.combineLabelDurationChartData();
    this.combinedLabelDurationMinAvgMaxChartData = this.combineLabelDurationMinAvgMaxChartData();
  }

  private aggregateListChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    this.listChartDataById.clear();

    this.lists.forEach((list) => {

      const listId = list.listId;

      const stats = this.stats!.lists[listId];

      if (!stats) {
        return;
      }

      const aggregation =
        this.listChartDataAggregator.aggregate(
          stats,
          this.selectedAggregationLevel,
        );

      this.listChartDataById.set(
        listId,
        aggregation,
      );
    });
  }

  private aggregateMemberChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    this.memberChartDataById.clear();

    this.members.forEach((member) => {

      const memberId = member.memberId;

      const stats = this.stats!.members[memberId];

      if (!stats) {
        return;
      }

      const aggregation =
        this.memberChartDataAggregator.aggregate(
          stats,
          this.selectedAggregationLevel,
        );

      this.memberChartDataById.set(
        memberId,
        aggregation,
      );
    });
  }

  private aggregateStageChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    this.stageChartDataById.clear();

    this.stages.forEach((stage) => {

      const stats = this.stats!.stages[stage];

      if (!stats) {
        return;
      }

      const aggregation =
        this.stageChartDataAggregator.aggregate(
          stats,
          this.selectedAggregationLevel,
        );

      this.stageChartDataById.set(
        stage,
        aggregation,
      );
    });
  }

  private aggregateCycleTimeByCompletionDate(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    const stats = this.stats!;
    const completedStats = stats.stages['completed'];

    if (!completedStats
      || !completedStats.stage
      || !completedStats.stage.movedIn) {

      this.cycleTimeByCompletionDateChartData = {
        chartData: {
          labels: [],
          min: [],
          avg: [],
          max: [],
        },
        primaryColor: 'rgba(153,102,255,0.6)',
        tableData: {},
      };

      return;
    }

    const movedInStats = completedStats.stage.movedIn;
    const timings = stats.board.timings || {};

    const completionCycles: { [date: string]: { [cardId: string]: number } } = {};

    for (const date in movedInStats) {

      const cardActionsByMember = movedInStats[date];
      if (!cardActionsByMember) {
        continue;
      }

      for (const memberId in cardActionsByMember) {

        const cardActions = cardActionsByMember[memberId];
        if (!cardActions || cardActions.length === 0) {
          continue;
        }

        cardActions.forEach((cardAction) => {

          const cardId = cardAction.cardId;
          const timing = timings[cardId];

          if (!timing) {
            return;
          }

          const cycleTimeMs = (timing.pendingDurationMs || 0) + (timing.inProgressDurationMs || 0);

          if (!completionCycles[date]) {
            completionCycles[date] = {};
          }

          completionCycles[date][cardId] = cycleTimeMs;
        });
      }
    }

    const labelledMinAvgMaxValues: LabelledMinAvgMaxValues = {
      labels: [],
      min: [],
      avg: [],
      max: [],
    };

    this.statisticsExtractor.aggregateNumericStatsByGroup(completionCycles)
      .forEach(({group, min, avg, max}) => {

        labelledMinAvgMaxValues.labels.push(group);

        labelledMinAvgMaxValues.min.push(min);
        labelledMinAvgMaxValues.avg.push(avg);
        labelledMinAvgMaxValues.max.push(max);
      });

    this.cycleTimeByCompletionDateChartData = {
      chartData: labelledMinAvgMaxValues,
      primaryColor: 'rgba(153,102,255,0.6)',
      tableData: completionCycles,
    };
  }

  private aggregateCycleTimeByMember(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    const stats = this.stats!;
    const completedStats = stats.stages['completed'];

    if (!completedStats
      || !completedStats.stage
      || !completedStats.stage.movedIn) {

      this.cycleTimeByMemberChartData = {
        chartData: {
          labels: [],
          min: [],
          avg: [],
          max: [],
        },
        primaryColor: 'rgba(153,102,255,0.6)',
        tableData: {},
      };

      return;
    }

    const movedInStats = completedStats.stage.movedIn;
    const timings = stats.board.timings || {};

    const completionCyclesByMember: { [memberId: string]: { [cardId: string]: number } } = {};

    for (const date in movedInStats) {

      const cardActionsByMember = movedInStats[date];
      if (!cardActionsByMember) {
        continue;
      }

      for (const memberId in cardActionsByMember) {

        const cardActions = cardActionsByMember[memberId];
        if (!cardActions || cardActions.length === 0) {
          continue;
        }

        cardActions.forEach((cardAction) => {

          const cardId = cardAction.cardId;
          const timing = timings[cardId];

          if (!timing) {
            return;
          }

          const cycleTimeMs = (timing.pendingDurationMs || 0) + (timing.inProgressDurationMs || 0);

          if (!completionCyclesByMember[memberId]) {
            completionCyclesByMember[memberId] = {};
          }

          completionCyclesByMember[memberId][cardId] = cycleTimeMs;
        });
      }
    }

    const labelledMinAvgMaxValues: LabelledMinAvgMaxValues = {
      labels: [],
      min: [],
      avg: [],
      max: [],
    };

    this.statisticsExtractor.aggregateNumericStatsByGroup(completionCyclesByMember)
      .forEach(({group, min, avg, max}) => {

        const memberId = group;
        const label = this.memberName(memberId) || memberId;

        labelledMinAvgMaxValues.labels.push(label);

        labelledMinAvgMaxValues.min.push(min);
        labelledMinAvgMaxValues.avg.push(avg);
        labelledMinAvgMaxValues.max.push(max);
      });

    this.cycleTimeByMemberChartData = {
      chartData: labelledMinAvgMaxValues,
      primaryColor: 'rgba(153,102,255,0.6)',
      tableData: completionCyclesByMember,
    };
  }

  private aggregateBoardActivityHeatmapData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    const boardStats = this.stats!.board;
    const aggregated: { [date: string]: any[] } = {};

    const addGroupedData = (groupedData: { [date: string]: { [key: string]: any[] } } | undefined): void => {

      if (!groupedData) {
        return;
      }

      for (const date in groupedData) {
        const memberActions = groupedData[date];

        for (const memberId in memberActions) {

          if (!aggregated[date]) {
            aggregated[date] = [];
          }

          aggregated[date] = aggregated[date].concat(memberActions[memberId]);
        }
      }
    };

    addGroupedData(boardStats.card.created);
    addGroupedData(boardStats.card.closed);
    addGroupedData(boardStats.list.movedIn);
    addGroupedData(boardStats.list.movedOut);
    addGroupedData(boardStats.label.added);
    addGroupedData(boardStats.label.removed);
    addGroupedData(boardStats.member.assigned);
    addGroupedData(boardStats.member.unassigned);
    addGroupedData(boardStats.comment.added);
    addGroupedData(boardStats.attachment.added);

    this.boardActivityHeatmapData = aggregated;
  }

  private calculateProgressionChartData(): void {

    if (!this.basicDataLoaded) {
      return;
    }

    const stats = this.stats!;

    const totalCards = this.statisticsExtractor.aggregateUniqueCount(stats.board.card.total.end) || 0;
    const pendingCards = this.statisticsExtractor.aggregateUniqueCount(stats.stages['pending']?.card.total.end) || 0;
    const inProgressCards = this.statisticsExtractor.aggregateUniqueCount(stats.stages['inProgress']?.card.total.end) || 0;

    const percentagePendingCards = Math.round((pendingCards / totalCards) * 100);
    const percentageInProgressCards = Math.round((inProgressCards / totalCards) * 100);
    const percentageCompletedCards = (100 - percentagePendingCards - percentageInProgressCards);

    this.progressionChartData = {
      'pending': {
        label: 'Pending',
        percentage: percentagePendingCards,
        backgroundColor: 'var(--ds-chart-red-bold)',
      },
      'inProgress': {
        label: 'In Progress',
        percentage: percentageInProgressCards,
        backgroundColor: 'var(--ds-chart-orange-bold)',
      },
      'completed': {
        label: 'Completed',
        percentage: percentageCompletedCards,
        backgroundColor: 'var(--ds-chart-green-bold)',
      },
    };
  }

  private combineLabelAddedChartDataByLevel(): ChartableData<LabelledValues, GroupedMap2D<CardReference[]>>[] {

    const combinedLabelChartData: ChartableData<LabelledValues, GroupedMap2D<CardReference[]>>[] = [];

    for (let [labelId, chartData] of this.labelChartDataById) {

      const labelColor = this.labelResolvedColor(labelId);

      const labelName = this.labelName(labelId);

      combinedLabelChartData.push({
        chartLabel: labelName,
        chartData: cloneDeep(chartData.added),
        primaryColor: labelColor,
        tableColumnLabel: labelName,
        tableData: cloneDeep(this.stats?.labels[labelId].label.added!),
      });
    }

    return combinedLabelChartData;
  }

  private combineLabelCountChartDataByLevel(): ChartableData<LabelledValues, GroupedMap<CardReference[]>>[] {

    const combinedLabelChartData: ChartableData<LabelledValues, GroupedMap<CardReference[]>>[] = [];

    for (let [labelId, chartData] of this.labelChartDataById) {

      const labelColor = this.labelResolvedColor(labelId);

      const labelName = this.labelName(labelId);

      combinedLabelChartData.push({
        chartLabel: labelName,
        chartData: cloneDeep(chartData.totalCards),
        primaryColor: labelColor,
        secondaryColor: labelColor,
        tableColumnLabel: labelName,
        tableData: cloneDeep(this.stats?.labels[labelId].label.total.day!),
      });
    }

    return combinedLabelChartData;
  }

  private combineLabelCountChartDataByPeriod(): ChartableData<LabelledValues, never> {

    const labelColors = [];

    const labelledValues = {
      labels: [],
      values: [],
    } as LabelledValues;

    for (let [labelId] of this.labelChartDataById) {

      const labelColor = this.labelResolvedColor(labelId);

      labelColors.push(labelColor);

      const labelName = this.labelName(labelId);

      const endCount = this.stats?.labels[labelId].label.total.end?.length || 0;

      labelledValues.labels.push(labelName);
      labelledValues.values.push(endCount);
    }

    return {
      chartData: labelledValues,
      chartLabel: 'Cards',
      primaryColor: labelColors,
    };
  }

  private combineLabelDurationChartData(): ChartableData<LabelledValues, GroupedMap2D<number>>[] {

    const combinedLabelChartData: ChartableData<LabelledValues, GroupedMap2D<number>>[] = [];

    for (let [labelId, chartData] of this.labelChartDataById) {

      const labelColor = this.labelResolvedColor(labelId);

      const labelName = this.labelName(labelId);

      combinedLabelChartData.push({
        chartLabel: labelName,
        chartData: cloneDeep(chartData.duration),
        primaryColor: labelColor,
        tableColumnLabel: labelName,
        tableData: cloneDeep(this.stats?.labels[labelId].label.duration!),
      });
    }

    return combinedLabelChartData;
  }

  private combineLabelDurationMinAvgMaxChartData(): ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>[] {

    const combinedLabelChartData: ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>[] = [];

    for (let [labelId, _] of this.labelChartDataById) {

      const labelColor = this.labelResolvedColor(labelId);

      const labelName = this.labelName(labelId);

      const labelledMinAvgMaxValues: LabelledMinAvgMaxValues = {
        labels: [],
        min: [],
        avg: [],
        max: [],
      };

      this.statisticsExtractor.aggregateNumericStatsByGroup(this.stats?.labels[labelId].label.duration!)
        .forEach(({group, min, avg, max}) => {

          labelledMinAvgMaxValues.labels.push(group);

          labelledMinAvgMaxValues.min.push(min);
          labelledMinAvgMaxValues.avg.push(avg);
          labelledMinAvgMaxValues.max.push(max);
        });

      combinedLabelChartData.push({
        chartLabel: labelName,
        chartData: labelledMinAvgMaxValues,
        primaryColor: labelColor,
        tableColumnLabel: labelName,
        tableData: cloneDeep(this.stats?.labels[labelId].label.duration!),
      });
    }

    return combinedLabelChartData;
  }

  private subscribeToBoardSummaryData() {

    this.store.select(selectBoardSummary)
      .pipe(untilDestroyed(this))
      .subscribe(stats => {
        this._stats = stats;
      });

    this.store.select(selectComparableBoardSummary)
      .pipe(untilDestroyed(this))
      .subscribe(stats => {
        this._comparableStats = stats;
      });

    this.store.select(selectBoardSummaryDateRange)
      .pipe(untilDestroyed(this))
      .subscribe(dateRange => {
        this._dateRange = dateRange;
      });

    this.store.select(selectComparableBoardSummaryDateRange)
      .pipe(untilDestroyed(this))
      .subscribe(dateRange => {
        this._comparableDateRange = dateRange;
      });

    this.store.select(selectLabels)
      .pipe(untilDestroyed(this))
      .subscribe(labels => {
        this._labels = labels;
      });

    this.store.select(selectLists)
      .pipe(untilDestroyed(this))
      .subscribe(lists => {
        this._lists = lists;
      });

    this.store.select(selectMembers)
      .pipe(untilDestroyed(this))
      .subscribe(members => {
        this._members = members;
      });

    this.store.select(selectBoardSummaryBasicLoaded)
      .pipe(untilDestroyed(this))
      .subscribe(loaded => {

        this._basicDataLoaded = loaded;

        if (loaded) {

          const dateRange = this.dateRange;

          if (dateRange) {
            this.pickBestAggregationLevel(dateRange);
          }

          this.aggregateChartData();
        }
      });

    this.store.select(selectBoardSummaryAllLoaded)
      .pipe(untilDestroyed(this))
      .subscribe(loaded => {
        this._allDataLoaded = loaded;
      });
  }

}
